/**
 * Shared campaign progress bar (dashboard + public). Bar fill caps at 100% visually.
 */
import { cn } from "@/lib/utils"

export interface CampaignProgressBarProps {
  raisedCents: number
  goalCents: number
  variant?: "default" | "compact"
  ended?: boolean
  className?: string
}

function formatMoney(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

export function CampaignProgressBar({
  raisedCents,
  goalCents,
  variant = "default",
  ended = false,
  className,
}: CampaignProgressBarProps) {
  const pct = goalCents > 0 ? (raisedCents / goalCents) * 100 : 0
  const barPct = Math.min(100, pct)
  const compact = variant === "compact"

  return (
    <div className={cn("w-full", className)} data-testid="campaign-progress-bar">
      {!compact && (
        <div className="mb-1 flex justify-between text-sm text-muted-foreground">
          <span>{formatMoney(raisedCents)} raised</span>
          <span>
            {formatMoney(goalCents)} goal
            {pct > 0 && (
              <span className="ml-2 tabular-nums text-foreground/80">
                ({Math.round(pct)}%)
              </span>
            )}
          </span>
        </div>
      )}
      {compact && (
        <div className="mb-1 text-xs text-muted-foreground">
          {formatMoney(raisedCents)} / {formatMoney(goalCents)}
        </div>
      )}
      <div
        className={cn(
          "h-2 w-full overflow-hidden rounded-full bg-muted",
          ended && "opacity-60"
        )}
      >
        <div
          className={cn(
            "h-full rounded-full transition-all duration-500",
            ended ? "bg-slate-400" : "bg-primary"
          )}
          style={{ width: `${barPct}%`, maxWidth: "100%" }}
        />
      </div>
    </div>
  )
}
